import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import './ApplicationsList.css';
import AdminLayout from './AdminLayout';

const PORTAL = '/ft-portal';

const Students = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    if (!searchTerm) { setFiltered(students); return; }
    const t = searchTerm.toLowerCase();
    setFiltered(students.filter(s =>
      s.full_name?.toLowerCase().includes(t) ||
      s.first_name?.toLowerCase().includes(t) ||
      s.last_name?.toLowerCase().includes(t) ||
      s.email?.toLowerCase().includes(t) ||
      s.phone?.toLowerCase().includes(t) ||
      s.area?.toLowerCase().includes(t) ||
      s.postcode?.toLowerCase().includes(t)
    ));
  }, [students, searchTerm]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const data = await api.get('/admin/students');
      setStudents(Array.isArray(data) ? data : data.students || []);
      setError(null);
    } catch (err) {
      setError('Failed to load students. The students feature may not be set up yet.');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  if (loading) {
    return (
      <div className="applications-list">
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Loading students...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout pageTitle="Students">
    <div className="applications-list">
      <div className="list-header">
        <h1>👥 All Students</h1>
      </div>

      {error && <div className="alert alert-error">⚠️ {error}</div>}

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="results-count">
          {filtered.length} of {students.length} students
        </div>
        <button className="ap-refresh-btn" onClick={fetchStudents} disabled={loading} title="Refresh">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transition: 'transform 0.4s', transform: loading ? 'rotate(360deg)' : 'none' }}>
            <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="table-container">
        <table className="applications-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Area</th>
              <th>Postcode</th>
              <th>Instructor</th>
              <th>Active</th>
              <th>Registered</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length > 0 ? (
              filtered.map((s) => (
                <tr key={s.id}>
                  <td>#{s.id}</td>
                  <td><strong>{s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim()}</strong></td>
                  <td>{s.email}</td>
                  <td>{s.phone || '—'}</td>
                  <td>{s.area || '—'}</td>
                  <td>{s.postcode || '—'}</td>
                  <td>{s.instructor_name || <span style={{ color: '#f59e0b' }}>Unassigned</span>}</td>
                  <td>
                    <span className={`status-badge ${s.is_active ? 'approved' : 'cancelled'}`}>
                      {s.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{s.created_at ? formatDate(s.created_at) : '—'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="no-data">
                  {error ? 'Error loading data.' : 'No students registered yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
    </AdminLayout>
  );
};

export default Students;
